import React, { useState, useEffect } from 'react';
import {
  FiBell, FiMail, FiSmartphone, FiMessageCircle, FiMoon,
  FiCheck, FiSend, FiLoader, FiToggleLeft, FiToggleRight,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const EVENTS = [
  { key: 'order_placed', label: 'Order Placed', icon: '🛍️' },
  { key: 'order_confirmed', label: 'Order Confirmed', icon: '✅' },
  { key: 'order_shipped', label: 'Order Shipped', icon: '🚚' },
  { key: 'order_delivered', label: 'Order Delivered', icon: '🎉' },
  { key: 'order_cancelled', label: 'Order Cancelled', icon: '❌' },
  { key: 'promotions', label: 'Promotions & Offers', icon: '🎁' },
  { key: 'price_drops', label: 'Price Drop Alerts', icon: '💰' },
  { key: 'review_reply', label: 'Review Replies', icon: '💬' },
];

const Toggle = ({ on, onChange, disabled }) => (
  <button onClick={() => !disabled && onChange(!on)} disabled={disabled}
    style={{
      background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
      color: on ? '#059669' : '#D1D5DB', opacity: disabled ? 0.4 : 1, transition: 'color 0.2s',
    }}>
    {on ? <FiToggleRight size={24} /> : <FiToggleLeft size={24} />}
  </button>
);

const NotificationPrefsPage = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSMS, setTestingSMS] = useState(false);
  const [testingWA, setTestingWA] = useState(false);
  const [phone, setPhone] = useState('');
  const [waPhone, setWaPhone] = useState('');

  useEffect(() => { fetchPrefs(); }, []);

  const fetchPrefs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notification-prefs');
      const data = res.data.data;
      setPrefs(data);
      setPhone(data.phone || '');
      setWaPhone(data.whatsappPhone || '');
    } catch { toast.error('Failed to load preferences'); }
    setLoading(false);
  };

  const savePrefs = async (updates) => {
    setSaving(true);
    try {
      const res = await api.put('/notification-prefs', updates);
      setPrefs(res.data.data);
      toast.success('Preferences saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const toggleChannel = (channel, value) => {
    const updated = { ...prefs.channels, [channel]: value };
    setPrefs(p => ({ ...p, channels: updated }));
    savePrefs({ channels: { [channel]: value } });
  };

  const toggleEvent = (event, channel, value) => {
    const updated = { ...prefs.events };
    if (!updated[event]) updated[event] = {};
    updated[event][channel] = value;
    setPrefs(p => ({ ...p, events: updated }));
    savePrefs({ events: { [event]: { [channel]: value } } });
  };

  const savePhone = () => savePrefs({ phone, whatsappPhone: waPhone });

  const testSMS = async () => {
    if (!phone) { toast.error('Enter a phone number first'); return; }
    setTestingSMS(true);
    try {
      const res = await api.post('/notification-prefs/test-sms', { phone });
      toast.success(res.data.message);
    } catch { toast.error('Test failed'); }
    setTestingSMS(false);
  };

  const testWhatsApp = async () => {
    if (!waPhone && !phone) { toast.error('Enter a phone number first'); return; }
    setTestingWA(true);
    try {
      const res = await api.post('/notification-prefs/test-whatsapp', { phone: waPhone || phone });
      toast.success(res.data.message);
    } catch { toast.error('Test failed'); }
    setTestingWA(false);
  };

  if (loading) return <div className="text-center py-5">Loading preferences...</div>;
  if (!prefs) return <div className="text-center py-5 text-muted">Unable to load preferences</div>;

  return (
    <div>
      <h4 style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <FiBell color="#B91C1C" /> Notification Preferences
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
        Choose how you'd like to be notified about your orders and promotions.
      </p>

      {/* Phone Numbers */}
      <div className="dashboard-card mb-4 p-4">
        <h6 style={{ fontWeight: 700, marginBottom: '16px' }}>📱 Phone Numbers</h6>
        <div className="row g-3">
          <div className="col-md-5">
            <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>SMS Number</label>
            <div className="input-group">
              <input className="form-control" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+1 555 000 0000" />
              <button className="btn btn-outline-danger" onClick={testSMS} disabled={testingSMS}>
                {testingSMS ? <FiLoader size={14} className="spin" /> : <FiSend size={14} />} Test
              </button>
            </div>
          </div>
          <div className="col-md-5">
            <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>WhatsApp Number</label>
            <div className="input-group">
              <input className="form-control" value={waPhone} onChange={e => setWaPhone(e.target.value)}
                placeholder="Same as SMS if empty" />
              <button className="btn btn-outline-success" onClick={testWhatsApp} disabled={testingWA}>
                {testingWA ? <FiLoader size={14} className="spin" /> : <FiSend size={14} />} Test
              </button>
            </div>
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button className="btn btn-danger w-100" onClick={savePhone} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Master Channel Toggles */}
      <div className="dashboard-card mb-4 p-4">
        <h6 style={{ fontWeight: 700, marginBottom: '16px' }}>🔔 Notification Channels</h6>
        <div className="d-flex flex-wrap gap-4">
          {[
            { key: 'email', label: 'Email', icon: <FiMail size={18} />, color: '#3B82F6' },
            { key: 'sms', label: 'SMS', icon: <FiSmartphone size={18} />, color: '#059669' },
            { key: 'whatsapp', label: 'WhatsApp', icon: <FiMessageCircle size={18} />, color: '#25D366' },
          ].map(ch => (
            <div key={ch.key} className="d-flex align-items-center gap-2"
              style={{ padding: '10px 16px', borderRadius: '10px', border: '1px solid #E5E7EB', background: prefs.channels?.[ch.key] ? `${ch.color}08` : '#FAFAFA' }}>
              <span style={{ color: ch.color }}>{ch.icon}</span>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>{ch.label}</span>
              <Toggle on={prefs.channels?.[ch.key]} onChange={v => toggleChannel(ch.key, v)} />
            </div>
          ))}
        </div>
      </div>

      {/* Event Matrix */}
      <div className="dashboard-card p-4">
        <h6 style={{ fontWeight: 700, marginBottom: '16px' }}>📋 Per-Event Settings</h6>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
            <thead>
              <tr style={{ fontSize: '12px', color: '#6B7280', fontWeight: 700 }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Event</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', width: '80px' }}><FiMail size={14} /> Email</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', width: '80px' }}><FiSmartphone size={14} /> SMS</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', width: '80px' }}><FiMessageCircle size={14} /> WA</th>
              </tr>
            </thead>
            <tbody>
              {EVENTS.map(evt => {
                const evtPrefs = prefs.events?.[evt.key] || {};
                return (
                  <tr key={evt.key} style={{ background: '#FAFAFA', borderRadius: '8px' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500, fontSize: '14px', borderRadius: '8px 0 0 8px' }}>
                      <span style={{ marginRight: '6px' }}>{evt.icon}</span>{evt.label}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Toggle on={evtPrefs.email !== false} onChange={v => toggleEvent(evt.key, 'email', v)}
                        disabled={!prefs.channels?.email} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Toggle on={evtPrefs.sms !== false} onChange={v => toggleEvent(evt.key, 'sms', v)}
                        disabled={!prefs.channels?.sms} />
                    </td>
                    <td style={{ textAlign: 'center', borderRadius: '0 8px 8px 0' }}>
                      <Toggle on={evtPrefs.whatsapp === true} onChange={v => toggleEvent(evt.key, 'whatsapp', v)}
                        disabled={!prefs.channels?.whatsapp} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-muted mt-3 mb-0" style={{ fontSize: '12px' }}>
          💡 Greyed out toggles mean the master channel is off. Enable the channel above to configure per-event settings.
        </p>
      </div>

      {/* Quiet Hours */}
      <div className="dashboard-card mt-4 p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 style={{ fontWeight: 700, margin: 0 }}><FiMoon size={16} className="me-2" />Quiet Hours</h6>
          <Toggle on={prefs.quietHours?.enabled} onChange={v => savePrefs({ quietHours: { enabled: v } })} />
        </div>
        {prefs.quietHours?.enabled && (
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '13px' }}>Start</label>
              <input type="time" className="form-control" value={prefs.quietHours.start || '22:00'}
                onChange={e => savePrefs({ quietHours: { start: e.target.value } })} />
            </div>
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '13px' }}>End</label>
              <input type="time" className="form-control" value={prefs.quietHours.end || '08:00'}
                onChange={e => savePrefs({ quietHours: { end: e.target.value } })} />
            </div>
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '13px' }}>Timezone</label>
              <select className="form-select" value={prefs.quietHours.timezone || 'America/New_York'}
                onChange={e => savePrefs({ quietHours: { timezone: e.target.value } })}>
                <option value="America/New_York">Eastern</option>
                <option value="America/Chicago">Central</option>
                <option value="America/Denver">Mountain</option>
                <option value="America/Los_Angeles">Pacific</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Europe/London">UK (GMT)</option>
              </select>
            </div>
            <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
              SMS and WhatsApp messages will be held and sent after quiet hours end.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPrefsPage;
